import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDateTime, formatNumber } from "../utils/format";

export default function ProductTimeline({ timeline }) {
  const navigate = useNavigate();
  const [expandedGroupedCounts, setExpandedGroupedCounts] = useState({});

  const toggleGroupedCount = (stockCountId) => {
    setExpandedGroupedCounts(prev => ({
      ...prev,
      [stockCountId]: !prev[stockCountId]
    }));
  };

  const getEventIcon = (eventType) => {
    switch (eventType) {
      case "purchase":
        return (
          <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
        );
      case "sale":
        return (
          <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case "price_update":
      case "selling_price_update":
        return (
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          </div>
        );
      case "stock_count":
        return (
          <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
    }
  };

  const getEventTitle = (event) => {
    switch (event.event_type) {
      case "purchase":
        return "Aankoop";
      case "sale":
        return "Verkoop";
      case "price_update":
      case "selling_price_update":
        return "Prijswijziging";
      case "stock_count":
        return "Voorraadtelling";
      default:
        return "Gebeurtenis";
    }
  };

  const renderEventDetails = (event) => {
    switch (event.event_type) {
      case "purchase":
        return (
          <div
            className="cursor-pointer hover:bg-gray-50 p-3 rounded -m-3"
            onClick={() => navigate(`/purchases/${event.invoice_id}`)}
          >
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">{event.supplier_name || "Onbekende leverancier"}</p>
                <p className="text-sm text-gray-600">Factuurnummer: #{event.invoice_id}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-600">+{formatNumber(event.quantity, 0)}</p>
                <p className="text-xs text-gray-500">stuks</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Aankoopprijs (excl. korting): </span>
                <span className="font-medium">€{formatNumber(event.price_excl_discount)}</span>
              </div>
              <div>
                <span className="text-gray-600">Aankoopprijs (incl. korting): </span>
                <span className="font-medium">€{formatNumber(event.price_incl_discount)}</span>
              </div>
            </div>
            {event.notes && (
              <p className="text-sm text-gray-600 mt-2 italic">{event.notes}</p>
            )}
          </div>
        );

      case "sale":
        return (
          <div>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">Verkocht door {event.sold_by}</p>
                <p className="text-sm text-gray-600">Verkoop #{event.sale_id}</p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-600">-{formatNumber(event.quantity, 0)}</p>
                <p className="text-xs text-gray-500">stuks</p>
              </div>
            </div>
            {(event.selling_price_excl_vat || event.selling_price_incl_vat) && (
              <div className="grid grid-cols-2 gap-2 text-sm">
                {event.selling_price_excl_vat && (
                  <div>
                    <span className="text-gray-600">Verkoopprijs (excl. BTW): </span>
                    <span className="font-medium">€{formatNumber(event.selling_price_excl_vat)}</span>
                  </div>
                )}
                {event.selling_price_incl_vat && (
                  <div>
                    <span className="text-gray-600">Verkoopprijs (incl. BTW): </span>
                    <span className="font-medium">€{formatNumber(event.selling_price_incl_vat)}</span>
                  </div>
                )}
              </div>
            )}
            {event.notes && (
              <p className="text-sm text-gray-600 italic">{event.notes}</p>
            )}
          </div>
        );

      case "price_update":
        return (
          <div>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">Manuele prijswijziging</p>
                {event.supplier_name && (
                  <p className="text-sm text-gray-600">Leverancier: {event.supplier_name}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Aankoopprijs (excl. korting): </span>
                {event.prev_price_excl_discount !== undefined ? (
                  <span>
                    <span className="line-through text-gray-400">€{formatNumber(event.prev_price_excl_discount)}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium text-purple-600">€{formatNumber(event.price_excl_discount)}</span>
                  </span>
                ) : (
                  <span className="font-medium">€{formatNumber(event.price_excl_discount)}</span>
                )}
              </div>
              <div>
                <span className="text-gray-600">Aankoopprijs (incl. korting): </span>
                {event.prev_price_incl_discount !== undefined ? (
                  <span>
                    <span className="line-through text-gray-400">€{formatNumber(event.prev_price_incl_discount)}</span>
                    <span className="mx-1">→</span>
                    <span className="font-medium text-purple-600">€{formatNumber(event.price_incl_discount)}</span>
                  </span>
                ) : (
                  <span className="font-medium">€{formatNumber(event.price_incl_discount)}</span>
                )}
              </div>
            </div>
            {event.notes && (
              <p className="text-sm text-gray-600 mt-2 italic">{event.notes}</p>
            )}
          </div>
        );

      case "selling_price_update":
        // Helper to render price with optional old→new format
        const renderPrice = (currentPrice, prevPrice) => {
          if (prevPrice !== undefined && prevPrice !== null && prevPrice !== currentPrice) {
            return (
              <span>
                <span className="line-through text-gray-400">€{formatNumber(prevPrice)}</span>
                <span className="mx-1">→</span>
                <span className="font-medium text-purple-600">€{formatNumber(currentPrice)}</span>
              </span>
            );
          }
          return <span className="font-medium">€{formatNumber(currentPrice)}</span>;
        };

        return (
          <div>
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="font-semibold text-gray-900">Manuele prijswijziging</p>
                {event.created_by && (
                  <p className="text-sm text-gray-600">Door: {event.created_by}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-600">Aankoopprijs (excl. korting): </span>
                {event.purchase_price_excl_discount != null ? (
                  renderPrice(event.purchase_price_excl_discount, event.prev_purchase_price_excl_discount)
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
              <div>
                <span className="text-gray-600">Aankoopprijs (incl. korting): </span>
                {event.purchase_price_incl_discount != null ? (
                  renderPrice(event.purchase_price_incl_discount, event.prev_purchase_price_incl_discount)
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
              <div>
                <span className="text-gray-600">Verkoopprijs (excl. BTW): </span>
                {event.selling_price_excl_vat != null ? (
                  renderPrice(event.selling_price_excl_vat, event.prev_selling_price_excl_vat)
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
              <div>
                <span className="text-gray-600">Verkoopprijs (incl. BTW): </span>
                {event.selling_price_incl_vat != null ? (
                  renderPrice(event.selling_price_incl_vat, event.prev_selling_price_incl_vat)
                ) : (
                  <span className="text-gray-400">-</span>
                )}
              </div>
            </div>
            {event.notes && (
              <p className="text-sm text-gray-600 mt-2 italic">{event.notes}</p>
            )}
          </div>
        );

      case "stock_count":
        const isGrouped = event.is_grouped && event.children && event.children.length > 0;
        const isExpanded = expandedGroupedCounts[event.stock_count_id];

        return (
          <div>
            <div
              className="cursor-pointer hover:bg-gray-50 p-3 rounded -m-3"
              onClick={(e) => {
                if (isGrouped) {
                  e.stopPropagation();
                  toggleGroupedCount(event.stock_count_id);
                } else {
                  navigate(`/stock-counts/${event.stock_count_id}`);
                }
              }}
            >
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  {isGrouped && (
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  )}
                  <div>
                    <p className="font-semibold text-gray-900">
                      {event.stock_count_name}
                      {isGrouped && (
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          (gegroepeerd uit {event.children.length} tellingen)
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-gray-600">
                      Telling #{event.stock_count_id}
                      {isGrouped && !isExpanded && (
                        <span className="ml-2 text-blue-600">Klik om uit te vouwen</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-orange-600">{formatNumber(event.quantity, 0)}</p>
                  <p className="text-xs text-gray-500">geteld</p>
                </div>
              </div>
            </div>

            {/* Child stock counts */}
            {isGrouped && isExpanded && (
              <div className="mt-3 ml-6 pl-4 border-l-2 border-orange-200 space-y-2">
                {event.children.map((child) => (
                  <div
                    key={child.stock_count_id}
                    className="cursor-pointer hover:bg-orange-50 p-2 rounded bg-orange-25"
                    onClick={() => navigate(`/stock-counts/${child.stock_count_id}`)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-sm font-medium text-gray-800">{child.stock_count_name}</p>
                        <p className="text-xs text-gray-500">Telling #{child.stock_count_id}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-orange-600">{formatNumber(child.quantity, 0)}</p>
                        <p className="text-xs text-gray-400">geteld</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return <p className="text-gray-600">Geen details beschikbaar</p>;
    }
  };

  if (!timeline || timeline.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        <svg className="w-16 h-16 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-lg font-medium">Nog geen geschiedenis</p>
        <p className="text-sm">Er zijn nog geen gebeurtenissen voor dit product geregistreerd.</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical line */}
      <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>

      {/* Timeline items */}
      <div className="space-y-6">
        {timeline.map((event, index) => (
          <div key={`${event.event_type}-${index}`} className="relative flex gap-4">
            {/* Icon */}
            <div className="relative z-10 flex-shrink-0">
              {getEventIcon(event.event_type)}
            </div>

            {/* Content */}
            <div className="flex-1 bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
              <div className="flex justify-between items-start mb-3">
                <h3 className="text-lg font-semibold text-gray-900">{getEventTitle(event)}</h3>
                <time className="text-sm text-gray-500">{formatDateTime(event.timestamp)}</time>
              </div>
              {renderEventDetails(event)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
